/* eslint-disable no-nested-ternary */
import {
  Button,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { unwrapResult } from '@reduxjs/toolkit';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CloseIcon from '@mui/icons-material/Close';
import { Content, ContentInteractionAction } from '../../../common/models/content/Content';
import { ContentArticleViewSkeleton } from './ContentArticleViewSkeleton';
import { getContentById, getContentStatus } from '../../../store/thunks/contentThunk';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { selectIsContentPreview } from '../../../store/slices/contentSlice';
import { UpdateShowDashboardToolbar } from '../../../store/slices/dashboardSlice';
import { TestIds } from '../../../assets/testIds';
import { HOME_ROUTE, RESOURCES_ROUTE } from '../../../routes/namedRoutes';
import { useCustomEventTracking } from '../../../common/useCustomEventTracking';
import { useIsDesktop } from '../../../common/hooks/useResponsive';
import { useSafeBack } from '../../../common/hooks/useSafeBack';
import { Notfound } from '../../NotFound';
import { ArticleContentDisplay } from './ArticleContentDisplay';
import ContentShareSheet from './ContentShareSheet';
import OptimusButton from '../../../common/library/Button/Button';
import PlaybookActions, { PlaybookNavState } from './PlaybookActions';
import { useTrackContentOpened } from '../../../common/hooks/useDisplayContentView';

const backBtnStyle = (theme: Theme) => ({
  gap: theme.spacing(0.625),
  padding: `${theme.spacing(0.9375)} ${theme.spacing(1.25)} ${theme.spacing(0.9375)} ${theme.spacing(0.9375)}`,
  minWidth: 0,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(0.5),
  fontSize: theme.spacing(1.5),
  lineHeight: theme.spacing(2),
  fontWeight: 400,
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.common.white,
  '& .MuiButton-startIcon': { m: 0 },
  '&:hover': {
    borderColor: theme.palette.divider,
    backgroundColor: theme.palette.action.hover,
  },
} as const);

export function ContentArticleView() {
  const { id } = useParams();
  const { t } = useTranslation();
  const theme = useTheme();
  const [content, setContent] = useState<Content>();
  const [hasError, setHasError] = useState(false);
  const dispatch = useAppDispatch();
  const { trackContentInteraction } = useCustomEventTracking();
  const isDesktop = useIsDesktop();
  const isContentPreview = useAppSelector(selectIsContentPreview);
  const [isSaved, setIsSaved] = useState(false);
  const safeBack = useSafeBack(HOME_ROUTE);
  const navigate = useNavigate();
  const location = useLocation();
  const trackContentOpened = useTrackContentOpened();

  const [isShareOpen, setIsShareOpen] = useState(false);

  useEffect(() => {
    dispatch(UpdateShowDashboardToolbar(true));
    return () => {
      // hide toolbar on unmount
      dispatch(UpdateShowDashboardToolbar(false));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Set on cleanup so a slower request for the previous playbook can't overwrite this one
    let stale = false;
    // Prev/Next Play skip the feed card's Open event, so it's sent once the play loads
    const fromPlaybookNav = !!(location.state as PlaybookNavState | null)?.fromPlaybookNav;
    setHasError(false);
    setContent(undefined);
    setIsSaved(false);
    dispatch(getContentById(Number(id))).then(unwrapResult)
      .then(async (contentRes) => {
        if (stale) return;
        // Playbooks have no Save button: their status comes from the playbook response API
        // (called by PlaybookActions), so the content state API is skipped for them
        if (contentRes?.attributes?.type !== 'playbook') {
          const contentStatusRes = await dispatch(getContentStatus(id || '')).then(unwrapResult);
          if (stale) return;
          if (contentStatusRes && contentStatusRes[Number(id)]) {
            setIsSaved(contentStatusRes[id || ''].save);
          }
        }
        setContent(contentRes);
        if (fromPlaybookNav) trackContentOpened(contentRes);
      })
      .catch(() => {
        if (!stale) setHasError(true);
      });
    return () => { stale = true; };
    // Refetch when Prev/Next Play navigates to another playbook
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fireContentInteractionEvent = (action: ContentInteractionAction, options?: {
    [k: string]: unknown;
  }) => {
    trackContentInteraction(
      action,
      content?.attributes?.type?.replace(/^\w/, (c) => c.toUpperCase()) || '',
      {
        contentId: content?.id as number,
        contentTitle: content?.attributes?.title || '',
        contentCreatorName: content?.attributes?.content_owner?.data?.attributes?.display_name || '',
        contentMetaData: content?.attributes?.metadata || [],
        type: content?.attributes?.type || '',
        action,
        ...options,
      },
    );
  };

  const fireContentSavedEvent = () => {
    setIsSaved(!isSaved);
    fireContentInteractionEvent(
      isSaved ? ContentInteractionAction.Unsave : ContentInteractionAction.Save,
    );
  };

  const articleActions = (
    <>
      <OptimusButton
        size='s'
        fullWidth={false}
        minWidth='auto'
        data-testid={TestIds.contentArticleView.saveBtn}
        customVariant={isSaved ? 'secondaryGray' : 'primary'}
        buttonText={isSaved ? `${t('navigation.saved')}` : `${t('content.action.save')}`}
        // Preview content isn't live, so it can't be saved or shared (same as the preview cards)
        disabled={isContentPreview}
        onClick={fireContentSavedEvent}
      />
      <OptimusButton
        size='s'
        fullWidth={false}
        minWidth='auto'
        data-testid={TestIds.contentArticleView.shareBtn}
        customVariant='secondaryGray'
        sx={{ borderColor: theme.palette.divider, color: theme.palette.text.secondary }}
        buttonText={`${t('content.action.share')}`}
        disabled={isContentPreview}
        onClick={() => setIsShareOpen(true)}
      />
    </>
  );

  const isPlaybook = content?.attributes?.type === 'playbook';

  return (
    <>
      <Box sx={{
        // On mobile the dashboard layout already pads the page by 16px
        px: theme.spacing(isDesktop ? 3 : 0.5),
        pt: theme.spacing(isDesktop ? 2 : 0),
        // Playbook buttons sit right-aligned on the same row as the back button
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1.25),
      }}>
        <Button
          data-testid={TestIds.contentArticleView.backBtn}
          startIcon={<ChevronLeftIcon sx={{ fontSize: `${theme.spacing(2)} !important` }} />}
          sx={backBtnStyle}
          // Playbooks always go to Resources, wherever the member came from
          onClick={isPlaybook ? () => navigate(RESOURCES_ROUTE) : safeBack}
        >
          {isPlaybook ? t('button.backToAllPlays') : t('button.backToFeed')}
        </Button>
        {isPlaybook && content &&
          <PlaybookActions contentId={content.id} disabled={isContentPreview} />
        }
      </Box>
      <Container maxWidth={false}
        disableGutters
        sx={{
          width: '100%',
          px: theme.spacing(4),
          pt: theme.spacing(4),
          paddingBottom: theme.spacing(6),
          backgroundColor: theme.palette.common.white,
          [theme.breakpoints.down('md')]: {
            p: `${theme.spacing(2.5)} ${theme.spacing(0.5)} ${theme.spacing(5)}`,
            marginTop: 0,
          },
        }}>
        {hasError ? <Notfound /> :
          !content ? <ContentArticleViewSkeleton showActions /> :
            <ArticleContentDisplay
              content={content}
              // Playbook actions live in the back-button row instead
              actions={isPlaybook ? undefined : articleActions}
            />
        }
      </Container >
      <Dialog
        open={isShareOpen}
        onClose={() => setIsShareOpen(false)}
        PaperProps={{
          sx: { borderRadius: theme.spacing(2), m: theme.spacing(3), width: theme.spacing(100) },
        }}
      >
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: theme.spacing(2, 3),
        }}>
          <Typography sx={{ fontSize: theme.spacing(2.5), fontWeight: 500 }}>
            {t('content.action.share')}
          </Typography>
          <IconButton aria-label={`${t('button.close')}`} onClick={() => setIsShareOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pb: theme.spacing(5) }}>
          <ContentShareSheet
            shareUrl={window.location.href}
            fireContentInteractionEvent={fireContentInteractionEvent}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
